import type { ComparisonArtifactSummary } from '../types/workbenchWitnessLayers'

export const ASCII_OR_ARABIC_INDIC_DIGITS = /[0-9\u0660-\u0669\u06F0-\u06F9]+/g

export const FOLIATION_OR_PAGE_PATTERNS: RegExp[] = [
  /fol(?:io)?\.?\s*[0-9\u0660-\u0669\u06F0-\u06F9]+\s*[rv]\.?/gi,
  /fol(?:io)?\.?(?=\s|$|[\u0600-\u06FF])/gi,
  /p(?:age)?\.?\s*[0-9\u0660-\u0669\u06F0-\u06F9]+/gi,
  /pdf\s*[0-9\u0660-\u0669\u06F0-\u06F9]+/gi
]
const FOLIATION_OR_PAGE_PATTERN = new RegExp(
  FOLIATION_OR_PAGE_PATTERNS.map(pattern => pattern.source).join('|'),
  'gi'
)

export const FOOTNOTE_MARKER_WITH_DIGITS = /[*†‡§؟?]?\s*[0-9\u0660-\u0669\u06F0-\u06F9]+/g
export const STANDALONE_NUMERIC_LINE = /(^|\n)\s*[0-9\u0660-\u0669\u06F0-\u06F9]+\s*(?=\n|$)/g
export const DOUBLE_SQUARE_BRACKET_CONTENT = /\[\[[\s\S]*?\]\]/g
export const SINGLE_SQUARE_BRACKET_CONTENT = /\[[^[\]]*?\]/g
export const ANGLE_BRACKET_CONTENT = /<[^>]*>/g
export const LACUNA_DOTS = /(?:\.\s*){3,}|…+/g
export const EDITORIAL_MARK_CHARS = /[*†‡§؟?]+/g
export const CURLY_BRACES_ONLY = /[{}]/g

function unwrapCurlyBraceContent(text: string): string {
  let current = text
  let next = current.replace(CURLY_BRACES_ONLY, '')
  while (next !== current) {
    current = next
    next = current.replace(CURLY_BRACES_ONLY, '')
  }
  return current
}

export function cleanTextForComparison(text: string): string {
  return analyzeTextForComparison(text).cleanedText
}

export function analyzeTextForComparison(text: string): {
  cleanedText: string
  artifactSummary: ComparisonArtifactSummary
} {
  if (!text) {
    return {
      cleanedText: '',
      artifactSummary: {
        foliationMarkers: [],
        foliationMarkerCount: 0,
        standaloneNumericLineCount: 0,
        footnoteMarkerCount: 0,
        squareBracketSegmentCount: 0,
        doubleSquareBracketSegmentCount: 0,
        angleBracketSegmentCount: 0,
        lacunaMarkerCount: 0,
        editorialMarkCount: 0,
        curlyBraceCount: 0
      }
    }
  }

  const foliationMatches = text.match(FOLIATION_OR_PAGE_PATTERN) || []
  const artifactSummary: ComparisonArtifactSummary = {
    foliationMarkers: Array.from(new Set(
      foliationMatches
        .map(match => match.trim())
        .filter(Boolean)
    )),
    foliationMarkerCount: foliationMatches.length,
    standaloneNumericLineCount: (text.match(STANDALONE_NUMERIC_LINE) || []).length,
    footnoteMarkerCount: (text.match(FOOTNOTE_MARKER_WITH_DIGITS) || []).length,
    squareBracketSegmentCount: (text.replace(DOUBLE_SQUARE_BRACKET_CONTENT, ' ').match(SINGLE_SQUARE_BRACKET_CONTENT) || []).length,
    doubleSquareBracketSegmentCount: (text.match(DOUBLE_SQUARE_BRACKET_CONTENT) || []).length,
    angleBracketSegmentCount: (text.match(ANGLE_BRACKET_CONTENT) || []).length,
    lacunaMarkerCount: (text.match(LACUNA_DOTS) || []).length,
    editorialMarkCount: (text.match(EDITORIAL_MARK_CHARS) || []).length,
    curlyBraceCount: (text.match(CURLY_BRACES_ONLY) || []).length
  }

  let cleaned = text.replace(/\r\n?/g, '\n')

  cleaned = cleaned.replace(DOUBLE_SQUARE_BRACKET_CONTENT, ' ')
  cleaned = cleaned.replace(SINGLE_SQUARE_BRACKET_CONTENT, ' ')
  cleaned = cleaned.replace(ANGLE_BRACKET_CONTENT, ' ')
  cleaned = cleaned.replace(LACUNA_DOTS, ' ')
  cleaned = unwrapCurlyBraceContent(cleaned)

  for (const pattern of FOLIATION_OR_PAGE_PATTERNS) {
    cleaned = cleaned.replace(pattern, ' ')
  }

  cleaned = cleaned.replace(STANDALONE_NUMERIC_LINE, '\n')
  cleaned = cleaned.replace(FOOTNOTE_MARKER_WITH_DIGITS, ' ')
  cleaned = cleaned.replace(ASCII_OR_ARABIC_INDIC_DIGITS, '')
  cleaned = cleaned.replace(EDITORIAL_MARK_CHARS, ' ')

  return {
    cleanedText: cleaned
      .replace(/\s+/g, ' ')
      .trim(),
    artifactSummary
  }
}

export function tokenizeComparisonWords(text: string): string[] {
  const cleaned = cleanTextForComparison(text)
  if (!cleaned) {
    return []
  }
  return cleaned.split(/\s+/)
}
